import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogDescription,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from "@/components/ui/standard-dialog";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MapboxLocationPicker, type LocationData } from "@/components/ui/mapbox-location-picker";

interface PlaceMapPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The place's own saved location (pin or drawn area) when editing it. */
  initialLocation?: LocationData;
  /** Where to open otherwise — a sub-space starts at its parent. */
  initialCenter?: { latitude: number; longitude: number };
  /**
   * May return a Promise (save straight away): the dialog then waits with a
   * spinner, closes on success and stays open on failure so the drawing is not
   * lost — the caller reports the error (toast) and rejects.
   */
  onConfirm: (location: LocationData) => void | Promise<void>;
}

/**
 * "Pick on map" for a place: drop a pin or draw an area (MapboxLocationPicker
 * `allowArea`). Mount conditionally — the picker runs address-lookup hooks.
 */
export function PlaceMapPickerDialog({
  open,
  onOpenChange,
  initialLocation,
  initialCenter,
  onConfirm,
}: PlaceMapPickerDialogProps) {
  const { t } = useTranslation("places");
  const [picked, setPicked] = useState<LocationData | null>(null);
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      await onConfirm(picked);
      onOpenChange(false);
    } catch {
      // Reported by the caller; stay open with the drawing intact.
    } finally {
      setSaving(false);
    }
  };

  return (
    <StandardDialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <StandardDialogContent
        size="4xl"
        // Esc on the map belongs to the map (it cancels an unfinished outline);
        // elsewhere in the dialog it still closes the dialog.
        onEscapeKeyDown={(e) => {
          if (e.target instanceof Element && e.target.closest(".mapboxgl-map")) e.preventDefault();
        }}
      >
        <StandardDialogHeader>
          <StandardDialogTitle>{t("mapPicker.title")}</StandardDialogTitle>
          <StandardDialogDescription>{t("mapPicker.description")}</StandardDialogDescription>
        </StandardDialogHeader>
        <StandardDialogBody>
          <MapboxLocationPicker
            allowArea
            initialLocation={initialLocation}
            initialCenter={initialCenter}
            onLocationSelect={setPicked}
            onClear={() => setPicked(null)}
            height="460px"
          />
        </StandardDialogBody>
        <StandardDialogFooter>
          <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            {t("mapPicker.cancel")}
          </Button>
          <Button type="button" disabled={!picked || saving} onClick={() => void confirm()}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t("mapPicker.confirm")}
          </Button>
        </StandardDialogFooter>
      </StandardDialogContent>
    </StandardDialog>
  );
}
